/**
 * MalnutritionRiskSuggestionCard — Renderer für 'malnutrition_risk'.
 *
 * Zeigt:
 *   - Score-Badge (low/moderate/high) + Tool-Kennzeichnung (NRS-2002/MUST)
 *   - Deterministischen Score (unveränderbar vom LLM)
 *   - LLM-generierte Begründung
 *   - Aktionen zur fachlichen Sichtung
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Stethoscope, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useAiCompanion } from '@/contexts/AiCompanionContext';
import {
  isMalnutritionRiskPayload,
  type AiSuggestion,
  type MalnutritionRiskSuggestionPayload,
} from '@/lib/ai/types';

interface Props {
  suggestion: AiSuggestion;
}

const RISK_STYLE: Record<MalnutritionRiskSuggestionPayload['risk_level'], string> = {
  low: 'bg-emerald-100 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-100',
  moderate: 'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-100',
  high: 'bg-rose-100 text-rose-900 dark:bg-rose-950 dark:text-rose-100',
};

const RISK_LABEL: Record<MalnutritionRiskSuggestionPayload['risk_level'], string> = {
  low: 'Scorebereich niedrig',
  moderate: 'Scorebereich mittel',
  high: 'Scorebereich hoch',
};

const TOOL_LABEL: Record<string, string> = {
  nrs_2002: 'NRS-2002',
  must: 'MUST',
  sga: 'SGA',
};

export const MalnutritionRiskSuggestionCard: React.FC<Props> = ({ suggestion }) => {
  const navigate = useNavigate();
  const { resolve } = useAiCompanion();

  if (!isMalnutritionRiskPayload(suggestion.payload)) return null;
  const p = suggestion.payload;

  const primary = p.suggested_actions[0];
  const secondary = p.suggested_actions.slice(1, 3);

  const handlePrimary = async () => {
    if (suggestion.patient_id) {
      navigate(`/patients/${suggestion.patient_id}?tab=klinik`);
    }
    await resolve(suggestion, 'accepted', primary?.kind ?? 'open_patient');
  };

  const handleDismiss = async () => {
    await resolve(suggestion, 'dismissed');
  };

  return (
    <article className="border-b p-4 last:border-b-0">
      <header className="mb-2 flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <Stethoscope className="h-4 w-4 text-violet-600" />
          <span
            className={cn(
              'rounded-md px-2 py-0.5 text-xs font-medium',
              RISK_STYLE[p.risk_level],
            )}
          >
            {RISK_LABEL[p.risk_level]}
          </span>
          <span className="text-xs text-muted-foreground">
            Screening-Score · {TOOL_LABEL[p.tool] ?? p.tool} = {p.deterministic_score}
          </span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Hinweis schließen"
          onClick={handleDismiss}
          className="h-6 w-6"
        >
          <X className="h-4 w-4" />
        </Button>
      </header>

      <p className="mb-3 text-sm text-foreground">{p.rationale}</p>

      <div className="flex flex-wrap items-center gap-2">
        {primary && (
          <Button size="sm" onClick={handlePrimary}>
            {primary.label}
            <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        )}
        {secondary.map((a, i) => (
          <Badge
            key={`${a.kind}-${i}`}
            variant="outline"
            className="text-xs font-normal"
          >
            {a.label}
          </Badge>
        ))}
      </div>
    </article>
  );
};
